/**
 * Every word the creator says, as data.
 *
 * The component ships English and nothing else. A host that has translated its page passes a
 * `messages` object and gets its own language back; a host that hasn't — which is every embed
 * today, and the native app — is unaffected, and its bundle carries exactly one language. That
 * is the reason this is a prop rather than a catalogue the package loads: the embed's whole
 * proposition is a small script on someone else's page, and thirteen languages nobody asked for
 * is not a feature, it is 40 KB.
 *
 * Two rules keep the shape honest:
 *
 *   - **Plurals go through `Intl.PluralRules`, never through `n === 1`.** English has two forms,
 *     Polish has four, Japanese has one. A catalogue that hard-codes the English shape cannot be
 *     translated into Polish without lying, so the count-bearing entries are `PluralForms` and
 *     the picking is the runtime's job.
 *   - **Validation messages are keyed by `code`, not looked up by their English text.** The
 *     validator in `@project/event-format` already stamps every problem with a stable code; this
 *     translates that code. Nothing here re-implements a rule, so a message can never disagree
 *     with the check that produced it.
 */

import { LIMITS, type EventProblem } from '@project/event-format';

/**
 * A count-bearing string in every form its language needs. `other` is required because it is the
 * one form every language has — CLDR guarantees it — so a partial translation still renders.
 */
export interface PluralForms {
  readonly zero?: string;
  readonly one?: string;
  readonly two?: string;
  readonly few?: string;
  readonly many?: string;
  readonly other: string;
}

export interface EventCreatorMessages {
  /** Group legends and headings. */
  readonly groupEvent: string;
  readonly groupWhen: string;
  readonly groupDownload: string;
  readonly groupInvitees: string;
  readonly groupAlerts: string;
  readonly groupDetails: string;

  /** Field labels and placeholders. */
  readonly title: string;
  readonly titlePlaceholder: string;
  readonly location: string;
  readonly allDay: string;
  readonly starts: string;
  readonly startTime: string;
  readonly ends: string;
  readonly endTime: string;
  readonly timeZone: string;
  readonly startTimeZone: string;
  readonly endTimeZone: string;
  readonly endsInAnotherZone: string;
  readonly duration: string;
  readonly durationDays: string;
  readonly durationHours: string;
  readonly durationMinutes: string;
  readonly url: string;
  readonly notes: string;

  /** The zone combobox. `{query}` is what the reader typed. */
  readonly zoneSearchPlaceholder: string;
  readonly zoneNoMatch: string;

  /** Hints under the time fields. `{place}` is a city/country name. */
  readonly hintFloating: string;
  readonly hintSplitZones: string;
  readonly hintEveryoneSees: string;
  readonly hintYourTimeZone: string;

  /** Repeat. */
  readonly repeat: string;
  readonly repeatNever: string;
  readonly repeatDaily: string;
  readonly repeatWeekly: string;
  readonly repeatBiweekly: string;
  readonly repeatMonthly: string;
  readonly repeatYearly: string;
  readonly repeatCustom: string;
  readonly frequency: string;
  readonly frequencyDaily: string;
  readonly frequencyWeekly: string;
  readonly frequencyMonthly: string;
  readonly frequencyYearly: string;
  readonly every: string;
  readonly endRepeat: string;
  readonly endRepeatNever: string;
  readonly endRepeatCount: string;
  readonly endRepeatUntil: string;
  readonly numberOfTimes: string;
  readonly lastDate: string;
  /** `Every 2 {unit}` — the unit an interval counts, by frequency and by count. */
  readonly unitDays: PluralForms;
  readonly unitWeeks: PluralForms;
  readonly unitMonths: PluralForms;
  readonly unitYears: PluralForms;
  /** The sentence under the repeat controls. `{every}` is one of the four above. */
  readonly repeatSummaryCount: string;
  readonly repeatSummaryUntil: string;
  readonly repeatSummaryForever: string;

  /** Invitees. */
  readonly addInvitee: string;
  readonly inviteePlaceholder: string;
  readonly add: string;
  readonly remove: string;
  /** `{email}` — the accessible name of a row's remove button. */
  readonly removeInvitee: string;
  readonly tallyInvited: PluralForms;

  /** Alerts. `{count}` is the number of minutes, hours, days or weeks. */
  readonly alertNone: string;
  readonly alertAtTime: string;
  readonly alertMinutesBefore: PluralForms;
  readonly alertHoursBefore: PluralForms;
  readonly alertDaysBefore: PluralForms;
  readonly alertWeeksBefore: PluralForms;
  /** Row labels: the first alert, then the second, and so on. `{index}` is a 1-based number. */
  readonly alertRowLabels: readonly string[];
  readonly alertRowFallback: string;
  readonly tallyAlerts: PluralForms;
  readonly showAs: string;
  readonly showAsBusy: string;
  readonly showAsFree: string;
  readonly tallyShownAsFree: string;
  readonly tallyLink: string;
  readonly tallyNotes: string;

  /** Download. */
  readonly downloadAs: string;
  readonly downloadEvent: string;
  readonly addToList: string;
  readonly copyText: string;
  readonly copied: string;
  readonly requiredHint: string;
  readonly addStraightToCalendar: string;
  /** `{format}` is the format's own name, which is not translated: jCal is jCal everywhere. */
  readonly previewHeading: string;
  readonly previewEmpty: string;

  /** The "describe it" box, shown only when the host supplies a proposer. */
  readonly describeHeading: string;
  readonly describeLabel: string;
  readonly describePlaceholder: string;
  readonly describeBusy: string;
  readonly describeAction: string;
  readonly describeFailed: string;

  /**
   * The one-line recap above the download button. Whole sentences rather than fragments joined
   * in code: word order is the first thing a translation changes, and a sentence assembled from
   * "{day}" + ", " + "{clock}" can only ever come out in English order.
   */
  readonly recapAllDay: string;
  readonly recapAllDayRange: string;
  readonly recapTimed: string;
  readonly recapTimedRange: string;
  readonly recapTimedRangeDays: string;
  readonly recapUntitled: string;
  readonly recapNoZone: string;
  readonly recapEveryOne: string;
  readonly recapEveryMany: string;
  readonly recapRepeats: string;
  readonly recapRepeatsUntil: string;
  readonly recapRepeatsCount: PluralForms;
  readonly recapInvitees: PluralForms;
  readonly recapAlertAtTime: string;
  readonly recapAlertOne: string;
  readonly recapAlerts: PluralForms;

  /**
   * Validation messages, keyed by the validator's `code` — or by `code:field` where one code
   * covers several messages, which `recurrence-invalid` and `too-many` both do. Lookup tries the
   * specific key first, so a translation may supply either. `{limit}` and `{field}` fill in.
   */
  readonly problems: Readonly<Record<string, string>>;
  /** Field names as they appear inside a `too-long` message. */
  readonly problemFields: Readonly<Record<string, string>>;
}

export const EN_MESSAGES: EventCreatorMessages = {
  groupEvent: 'Event',
  groupWhen: 'When',
  groupDownload: 'Download',
  groupInvitees: 'Invitees',
  groupAlerts: 'Alerts',
  groupDetails: 'Details',

  title: 'Title',
  titlePlaceholder: 'Team standup',
  location: 'Location',
  allDay: 'All-day',
  starts: 'Starts',
  startTime: 'Start time',
  ends: 'Ends',
  endTime: 'End time',
  timeZone: 'Time zone',
  startTimeZone: 'Start time zone',
  endTimeZone: 'End time zone',
  endsInAnotherZone: 'Ends in a different time zone',
  duration: 'Duration',
  durationDays: 'Days',
  durationHours: 'Hours',
  durationMinutes: 'Minutes',
  url: 'URL',
  notes: 'Notes',

  zoneSearchPlaceholder: 'Search city, country, or time zone',
  zoneNoMatch: 'No time zones match “{query}”.',

  hintFloating: 'Opens at this clock time wherever the event is added.',
  hintSplitZones:
    'The start and end each carry their own zone, so a flight that leaves at 15:00 in one place and lands at 11:00 in another reads correctly in a calendar.',
  hintEveryoneSees: 'Everyone sees this time in {place}',
  hintYourTimeZone: ' — your time zone',

  repeat: 'Repeat',
  repeatNever: 'Never',
  repeatDaily: 'Every day',
  repeatWeekly: 'Every week',
  repeatBiweekly: 'Every 2 weeks',
  repeatMonthly: 'Every month',
  repeatYearly: 'Every year',
  repeatCustom: 'Custom…',
  frequency: 'Frequency',
  frequencyDaily: 'Daily',
  frequencyWeekly: 'Weekly',
  frequencyMonthly: 'Monthly',
  frequencyYearly: 'Yearly',
  every: 'Every',
  endRepeat: 'End repeat',
  endRepeatNever: 'Never',
  endRepeatCount: 'After a number of times',
  endRepeatUntil: 'On a date',
  numberOfTimes: 'Number of times',
  lastDate: 'Last date',
  unitDays: { one: 'day', other: 'days' },
  unitWeeks: { one: 'week', other: 'weeks' },
  unitMonths: { one: 'month', other: 'months' },
  unitYears: { one: 'year', other: 'years' },
  repeatSummaryCount: '{every}, {count} times.',
  repeatSummaryUntil: '{every}, until {date}.',
  repeatSummaryForever: '{every}, with no end — your calendar can stop it later.',

  addInvitee: 'Add an invitee',
  inviteePlaceholder: 'name@example.com',
  add: 'Add',
  remove: 'Remove',
  removeInvitee: 'Remove {email}',
  tallyInvited: { other: '{count} invited' },

  alertNone: 'None',
  alertAtTime: 'At time of event',
  alertMinutesBefore: { one: '{count} minute before', other: '{count} minutes before' },
  alertHoursBefore: { one: '{count} hour before', other: '{count} hours before' },
  alertDaysBefore: { one: '{count} day before', other: '{count} days before' },
  alertWeeksBefore: { one: '{count} week before', other: '{count} weeks before' },
  alertRowLabels: ['Alert', 'Second alert', 'Third alert', 'Fourth alert', 'Fifth alert'],
  alertRowFallback: 'Alert {index}',
  tallyAlerts: { one: '{count} alert', other: '{count} alerts' },
  showAs: 'Show as',
  showAsBusy: 'Busy',
  showAsFree: 'Free',
  tallyShownAsFree: 'shown as free',
  tallyLink: 'a link',
  tallyNotes: 'notes',

  downloadAs: 'Download as',
  downloadEvent: 'Download event',
  addToList: 'Add to the list',
  copyText: 'Copy text',
  copied: 'Copied',
  requiredHint: 'Needed before this event can be downloaded.',
  addStraightToCalendar: 'Add straight to a calendar',
  previewHeading: '{format} preview',
  previewEmpty: 'Fill in the event to see the file.',

  describeHeading: 'Describe it',
  describeLabel: 'Describe your event',
  describePlaceholder: 'Lunch with Sam next Tuesday at 1, at Cafe Kranzler',
  describeBusy: 'Reading…',
  describeAction: 'Fill in the form',
  describeFailed: "That didn't work — fill the event in below.",

  recapAllDay: '{day}, all day',
  recapAllDayRange: '{start} to {end}, all day',
  recapTimed: '{day}, {clock}',
  recapTimedRange: '{day}, {start}–{end}',
  recapTimedRangeDays: '{startDay} {startClock} – {endDay} {endClock}',
  recapUntitled: 'This event',
  recapNoZone: 'no zone',
  recapEveryOne: 'every {unit}',
  recapEveryMany: 'every {count} {unit}',
  recapRepeats: 'repeats {every}',
  recapRepeatsUntil: 'repeats {every} until {date}',
  recapRepeatsCount: { other: 'repeats {every}, {count} times' },
  recapInvitees: { one: '{count} invitee', other: '{count} invitees' },
  recapAlertAtTime: 'at the time of the event',
  recapAlertOne: 'alert {words}',
  recapAlerts: { one: '{count} alert', other: '{count} alerts' },

  problems: {
    'title-missing': 'An event needs a title.',
    'too-long': 'Keep the {field} under {limit} characters.',
    'too-many:attendees': 'An event can carry up to {limit} guests.',
    'too-many:remindersMinutesBefore': 'An event can carry up to {limit} reminders.',
    'too-many:recurrence.count': 'A repeat can run at most {limit} times.',
    'start-invalid': 'Start is not a valid date or time.',
    'end-invalid': 'End is not a valid date or time.',
    'end-kind-mismatch': 'An event is either all-day or timed — start and end must match.',
    'end-before-start': 'End is before start.',
    'url-invalid': 'URL must start with http:// or https://.',
    'reminder-invalid': 'A reminder is a whole number of minutes before the event.',
    'recurrence-invalid:recurrence.interval': 'Repeat interval must be 1 or more.',
    'recurrence-invalid:recurrence':
      'A repeat ends after a number of times or on a date, not both.',
    'recurrence-invalid:recurrence.count': 'Repeat count must be 1 or more.',
    'recurrence-invalid:recurrence.until': 'Repeat end date must look like 2026-08-15.',
    // The address is not quoted back: this renders directly beneath the row holding it.
    'email-invalid': 'Not an email address.',
  },
  problemFields: {
    title: 'title',
    location: 'location',
    description: 'notes',
    url: 'URL',
  },
};

/** A host's partial catalogue over the English one. Anything untranslated stays English. */
export function mergeMessages(
  overrides: Partial<EventCreatorMessages> | undefined,
): EventCreatorMessages {
  if (overrides === undefined) return EN_MESSAGES;
  return {
    ...EN_MESSAGES,
    ...overrides,
    problems: { ...EN_MESSAGES.problems, ...overrides.problems },
    problemFields: { ...EN_MESSAGES.problemFields, ...overrides.problemFields },
  };
}

/** `{name}` → value. Anything the caller doesn't supply is left as it stands. */
export function fill(template: string, values: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in values ? String(values[name]) : whole,
  );
}

/**
 * The form of a count-bearing string this language uses for this number.
 *
 * `Intl.PluralRules` decides, so Polish gets its four forms and Japanese its one, and a catalogue
 * that supplies only `other` still renders — which is what makes a half-finished translation
 * degrade to slightly-wrong grammar rather than to a missing string.
 */
export function plural(
  forms: PluralForms,
  count: number,
  locale: string | undefined,
  values: Readonly<Record<string, string | number>> = {},
): string {
  let rule: Intl.LDMLPluralRule = 'other';
  try {
    rule = new Intl.PluralRules(locale).select(count);
  } catch {
    // An unparseable tag, or a runtime without PluralRules: `other` is every language's fallback.
  }
  return fill(forms[rule] ?? forms.other, { count, ...values });
}

/**
 * The ceiling a `too-long`/`too-many` message names, by the field it was raised against. Read
 * from the validator's own limits rather than written out here, so a message cannot promise a
 * number the check does not enforce.
 */
const LIMIT_FOR: Readonly<Record<string, number>> = {
  title: LIMITS.title,
  location: LIMITS.location,
  description: LIMITS.description,
  url: LIMITS.url,
  attendees: LIMITS.attendees,
  remindersMinutesBefore: LIMITS.reminders,
  'recurrence.count': LIMITS.recurrenceCount,
};

/**
 * A validation problem in the reader's language, falling back to the validator's own English.
 *
 * The fallback is the point of the design: a catalogue that has not been updated for a newly
 * added check still shows the check's own message, in English, rather than a key or a blank.
 */
export function problemMessage(problem: EventProblem, messages: EventCreatorMessages): string {
  // `attendees[0].email` and `remindersMinutesBefore[3]` are one message each, not one per row.
  const field = problem.field.replace(/\[\d+\]/g, '');
  const template = messages.problems[`${problem.code}:${field}`] ?? messages.problems[problem.code];
  if (template === undefined) return problem.message;
  return fill(template, {
    field: messages.problemFields[field] ?? field,
    limit: LIMIT_FOR[field] ?? 0,
  });
}
